import * as React from 'react'
import { setAgentHighlight } from '../editor/node_focus_store'
import {
  activatePublicLayer,
  setMapOutputHighlight,
  useMapOutputHighlightSnapshot,
} from '../editor/map_output_highlight'

/**
 * Inline `#42`-style reference link rendered inside chat bubbles / tool
 * events. Hovering (or keyboard-focusing) it highlights the matching node
 * on the canvas via the shared focus store; clicking asks the editor to
 * pan to it. Pure view affordance — safe to render even when the editor
 * isn't ready (hover just highlights nothing, click no-ops).
 */
export function ChatNodeRef({
  id,
  label,
  layerId,
  onActivate,
}: {
  id: number
  label?: string
  layerId?: number
  onActivate?: (id: number) => void
}) {
  const linkableIds = useMapOutputHighlightSnapshot().linkableIds
  const publicView = linkableIds != null
  const namedLayer = layerId != null || label != null
  if (publicView && !namedLayer && !linkableIds.has(id)) {
    return <>#{id}</>
  }
  const show = () => {
    if (layerId != null && (id === 0 || (publicView && !linkableIds.has(id)))) return
    if (publicView) setMapOutputHighlight(id)
    else setAgentHighlight(id)
  }
  const hide = () => {
    if (publicView) setMapOutputHighlight(null)
    else setAgentHighlight(null)
  }
  return (
    <button
      type="button"
      className="model-editor-chat-ref"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={() => {
        if (layerId != null) activatePublicLayer(layerId)
        else onActivate?.(id)
      }}
      title={publicView || namedLayer ? "Show this layer" : `Highlight node #${id} on the canvas`}
    >
      {label || `#${id}`}
    </button>
  )
}

const MD_MONO =
  'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace'

function safeHttpUrl(url: string): string | null {
  try {
    const parsed = new URL(url, window.location.origin)
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null
    return parsed.href
  } catch {
    return null
  }
}

/**
 * Render the *inline* markdown the agent tends to emit within a single line:
 *   • `` `code` ``  → <code>
 *   • `[text](url)` → external link
 *   • `**bold**`    → <strong>
 *   • `~~strike~~`  → <del>
 *   • `*italic*`    → <em>
 *   • `#<id>`       → interactive node-reference link (see ChatNodeRef)
 */
function renderInline(
  text: string,
  onActivateRef: ((id: number) => void) | undefined,
  keyPrefix: string,
  nodeRefs = true
): React.ReactNode[] {
  const re =
    /(`[^`]+`)|(!\[([^\]]*)\]\(([^)\s]+)\))|(\[([^\]]+)\]\(([^)\s]+)\))|(\*\*[\s\S]+?\*\*)|(~~[\s\S]+?~~)|(\*(?!\s)[^*\n]+?\*)|(#\d+)|(@\[([^\]]+)\]\((?:#(\d+)|layer:(\d+))\))/g
  const out: React.ReactNode[] = []
  let last = 0
  let m: RegExpExecArray | null
  let k = 0
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const key = `${keyPrefix}-i${k++}-${m.index}`
    if (m[1]) {
      out.push(
        <code
          key={key}
          style={{
            padding: "1px 4px",
            borderRadius: 4,
            background: "rgba(0,0,0,0.06)",
            fontFamily: MD_MONO,
            fontSize: "0.92em",
          }}
        >
          {m[1].slice(1, -1)}
        </code>
      )
    } else if (m[2]) {
      const src = safeHttpUrl(m[4])
      out.push(src ? (
        <img
          key={key}
          src={src}
          alt={m[3]}
          style={{ maxWidth: "100%", height: "auto", display: "block", margin: "8px auto", borderRadius: 8 }}
        />
      ) : (
        m[0]
      ))
    } else if (m[5]) {
      const href = safeHttpUrl(m[7])
      out.push(href ? (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline", textUnderlineOffset: 2 }}
        >
          {renderInline(m[6], onActivateRef, `${key}-lnk`, nodeRefs)}
        </a>
      ) : (
        m[0]
      ))
    } else if (m[8]) {
      out.push(<strong key={key}>{renderInline(m[8].slice(2, -2), onActivateRef, key, nodeRefs)}</strong>)
    } else if (m[9]) {
      out.push(
        <del key={key} style={{ opacity: 0.75 }}>
          {renderInline(m[9].slice(2, -2), onActivateRef, key, nodeRefs)}
        </del>
      )
    } else if (m[10]) {
      out.push(<em key={key}>{renderInline(m[10].slice(1, -1), onActivateRef, key, nodeRefs)}</em>)
    } else if (m[11]) {
      if (nodeRefs) {
        out.push(<ChatNodeRef key={key} id={Number(m[11].slice(1))} onActivate={onActivateRef} />)
      } else {
        out.push(m[11])
      }
    } else if (m[12]) {
      const nodeId = m[14] ? Number(m[14]) : 0
      const linkedLayerId = m[15] ? Number(m[15]) : undefined
      if (nodeRefs) {
        out.push(
          <ChatNodeRef
            key={key}
            id={nodeId}
            label={m[13]}
            layerId={linkedLayerId}
            onActivate={onActivateRef}
          />
        )
      } else {
        out.push(m[13])
      }
    }
    last = m.index + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/**
 * Inline-only render (no block elements). Used for compact, single-line
 * surfaces like the tool-event pills where headings / lists would be
 * out of place. Returns the raw string when nothing matched so the common
 * case stays a plain text node.
 */
export function renderTextWithRefs(text: string, onActivateRef?: (id: number) => void): React.ReactNode {
  const nodes = renderInline(text, onActivateRef, "inl")
  if (nodes.length === 0) return text
  if (nodes.length === 1 && typeof nodes[0] === "string") return nodes[0]
  return nodes
}

const MD_HEADING_SIZE: Record<number, number> = { 1: 16, 2: 14.5, 3: 13.5, 4: 12.5, 5: 12, 6: 11.5 }

function MdHeading({
  level,
  first,
  children,
  sizes,
}: {
  level: number
  first: boolean
  children: React.ReactNode
  sizes?: Record<number, number>
}) {
  const Tag = `h${Math.min(Math.max(level, 1), 6)}` as keyof JSX.IntrinsicElements
  const scale = sizes ?? MD_HEADING_SIZE
  return (
    <Tag
      style={{
        fontSize: scale[level] ?? 12.5,
        fontWeight: 700,
        lineHeight: 1.3,
        margin: `${first ? 0 : 8}px 0 4px`,
      }}
    >
      {children}
    </Tag>
  )
}

function isMarkdownTableRow(line: string): boolean {
  const trimmed = line.trim()
  return trimmed.includes("|") && !trimmed.startsWith("```")
}

function isMarkdownTableSeparator(line: string): boolean {
  const trimmed = line.trim()
  if (!trimmed.includes("|")) return false
  return trimmed
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .every(cell => /^:?-{3,}:?$/.test(cell.trim()))
}

function parseMarkdownTableCells(line: string): string[] {
  let row = line.trim()
  if (row.startsWith("|")) row = row.slice(1)
  if (row.endsWith("|")) row = row.slice(0, -1)
  return row.split("|").map(cell => cell.trim())
}

function renderMarkdownCodeBlock(
  code: string,
  language: string | undefined,
  blockKey: string
): React.ReactNode {
  return (
    <div key={blockKey} style={{ margin: "6px 0" }}>
      {language ? (
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "#6c757d",
            marginBottom: 4,
          }}
        >
          {language}
        </div>
      ) : null}
      <pre
        style={{
          margin: 0,
          padding: "8px 10px",
          borderRadius: 8,
          background: "rgba(0,0,0,0.05)",
          border: "1px solid rgba(0,0,0,0.08)",
          overflowX: "auto",
          whiteSpace: "pre",
          wordBreak: "normal",
          fontFamily: MD_MONO,
          fontSize: 11,
          lineHeight: 1.45,
        }}
      >
        <code>{code}</code>
      </pre>
    </div>
  )
}

function renderMarkdownTable(
  header: string[],
  rows: string[][],
  blockKey: string,
  onActivateRef?: (id: number) => void
): React.ReactNode {
  const colCount = Math.max(header.length, ...rows.map(row => row.length), 1)
  const normalizedHeader = Array.from({ length: colCount }, (_, idx) => header[idx] ?? "")
  const normalizedRows = rows.map(row =>
    Array.from({ length: colCount }, (_, idx) => row[idx] ?? "")
  )
  const thStyle: React.CSSProperties = {
    padding: "5px 8px",
    borderBottom: "2px solid rgba(0,0,0,0.12)",
    textAlign: "left",
    fontWeight: 700,
    verticalAlign: "top",
    whiteSpace: "nowrap",
  }
  const tdStyle: React.CSSProperties = {
    padding: "5px 8px",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    verticalAlign: "top",
  }
  return (
    <div
      key={blockKey}
      style={{
        margin: "6px 0",
        overflowX: "auto",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 8,
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 11.5,
          lineHeight: 1.4,
        }}
      >
        <thead>
          <tr>
            {normalizedHeader.map((cell, idx) => (
              <th key={`${blockKey}-h-${idx}`} style={thStyle}>
                {renderInline(cell, onActivateRef, `${blockKey}-h-${idx}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {normalizedRows.map((row, rowIdx) => (
            <tr key={`${blockKey}-r-${rowIdx}`}>
              {row.map((cell, cellIdx) => (
                <td key={`${blockKey}-r-${rowIdx}-c-${cellIdx}`} style={tdStyle}>
                  {renderInline(cell, onActivateRef, `${blockKey}-r-${rowIdx}-c-${cellIdx}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Lightweight block-level Markdown renderer for assistant replies. Supports
 * ATX headings, lists, horizontal rules, fenced code blocks, pipe tables,
 * blockquotes, pictures (`![alt](url)`), and paragraphs — with inline
 * formatting handled by `renderInline`.
 */
export function renderMarkdown(
  text: string,
  onActivateRef?: (id: number) => void,
  options?: { headingSizes?: Record<number, number> }
): React.ReactNode {
  const lines = text.replace(/\r\n/g, "\n").split("\n")
  const blocks: React.ReactNode[] = []
  let para: string[] = []
  let key = 0

  const flushPara = () => {
    if (!para.length) return
    const inline: React.ReactNode[] = []
    para.forEach((ln, idx) => {
      if (idx > 0) inline.push(<br key={`br-${key}-${idx}`} />)
      inline.push(...renderInline(ln, onActivateRef, `p-${key}-${idx}`))
    })
    blocks.push(
      <p key={`p-${key++}`} style={{ margin: blocks.length === 0 ? "0 0 6px" : "6px 0" }}>
        {inline}
      </p>
    )
    para = []
  }

  let i = 0
  while (i < lines.length) {
    const raw = lines[i]
    const trimmed = raw.trim()

    if (!trimmed) {
      flushPara()
      i++
      continue
    }

    if (trimmed.startsWith("```")) {
      flushPara()
      const language = trimmed.slice(3).trim()
      i++
      const codeLines: string[] = []
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i])
        i++
      }
      if (i < lines.length) i++
      blocks.push(renderMarkdownCodeBlock(codeLines.join("\n"), language || undefined, `code-${key++}`))
      continue
    }

    if (
      isMarkdownTableRow(raw) &&
      i + 1 < lines.length &&
      isMarkdownTableSeparator(lines[i + 1])
    ) {
      flushPara()
      const header = parseMarkdownTableCells(raw)
      i += 2
      const rows: string[][] = []
      while (i < lines.length && isMarkdownTableRow(lines[i]) && lines[i].trim()) {
        rows.push(parseMarkdownTableCells(lines[i]))
        i++
      }
      blocks.push(renderMarkdownTable(header, rows, `table-${key++}`, onActivateRef))
      continue
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.*)$/)
    if (heading) {
      flushPara()
      const level = heading[1].length
      blocks.push(
        <MdHeading key={`h-${key++}`} level={level} first={blocks.length === 0} sizes={options?.headingSizes}>
          {renderInline(heading[2], onActivateRef, `h-${key}`)}
        </MdHeading>
      )
      i++
      continue
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      flushPara()
      blocks.push(
        <hr
          key={`hr-${key++}`}
          style={{ border: 0, borderTop: "1px solid rgba(0,0,0,0.12)", margin: "8px 0" }}
        />
      )
      i++
      continue
    }

    if (/^>\s?/.test(trimmed)) {
      flushPara()
      const quoteLines: string[] = []
      while (i < lines.length && /^>\s?/.test(lines[i].trim())) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ""))
        i++
      }
      blocks.push(
        <blockquote
          key={`bq-${key++}`}
          style={{
            margin: "6px 0",
            padding: "6px 10px",
            borderLeft: "3px solid rgba(59, 74, 135, 0.35)",
            background: "rgba(59, 74, 135, 0.05)",
            color: "inherit",
          }}
        >
          {quoteLines.map((line, idx) => (
            <p key={`bq-${key}-${idx}`} style={{ margin: idx === 0 ? 0 : "4px 0 0" }}>
              {renderInline(line, onActivateRef, `bq-${key}-${idx}`)}
            </p>
          ))}
        </blockquote>
      )
      continue
    }

    if (/^[-*+]\s+/.test(trimmed)) {
      flushPara()
      const items: React.ReactNode[] = []
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        const content = lines[i].replace(/^\s*[-*+]\s+/, "")
        items.push(
          <li key={`li-${key}-${items.length}`}>{renderInline(content, onActivateRef, `li-${key}-${items.length}`)}</li>
        )
        i++
      }
      blocks.push(
        <ul key={`ul-${key++}`} style={{ margin: "4px 0 6px", paddingLeft: 18 }}>
          {items}
        </ul>
      )
      continue
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      flushPara()
      const items: React.ReactNode[] = []
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        const content = lines[i].replace(/^\s*\d+\.\s+/, "")
        items.push(
          <li key={`oli-${key}-${items.length}`}>{renderInline(content, onActivateRef, `oli-${key}-${items.length}`)}</li>
        )
        i++
      }
      blocks.push(
        <ol key={`ol-${key++}`} style={{ margin: "4px 0 6px", paddingLeft: 18 }}>
          {items}
        </ol>
      )
      continue
    }

    para.push(trimmed)
    i++
  }
  flushPara()

  if (blocks.length === 0) return text
  return blocks
}

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/**
 * Reveals assistant reply text progressively (character-by-character) so new
 * responses feel like they stream in. Existing/historical messages render
 * instantly; honours `prefers-reduced-motion`.
 */
export function GradualAssistantReply({
  text,
  animate,
  onActivateRef,
  onRevealTick,
}: {
  text: string
  animate: boolean
  onActivateRef?: (id: number) => void
  onRevealTick?: () => void
}) {
  const [visibleCount, setVisibleCount] = React.useState(() =>
    animate && !prefersReducedMotion() ? 0 : text.length
  )
  const [isRevealing, setIsRevealing] = React.useState(
    () => animate && !prefersReducedMotion() && text.length > 0
  )

  React.useEffect(() => {
    if (!animate || prefersReducedMotion() || text.length === 0) {
      setVisibleCount(text.length)
      setIsRevealing(false)
      return
    }

    setVisibleCount(0)
    setIsRevealing(true)

    const durationMs = Math.min(2800, Math.max(450, text.length * 14))
    const start = performance.now()
    let frame = 0

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs)
      const nextCount = Math.max(0, Math.min(text.length, Math.ceil(progress * text.length)))
      setVisibleCount(nextCount)
      onRevealTick?.()

      if (progress < 1) {
        frame = requestAnimationFrame(tick)
      } else {
        setIsRevealing(false)
      }
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [animate, text, onRevealTick])

  const revealed = text.slice(0, visibleCount)

  return (
    <span className={isRevealing ? "model-editor-chat-reveal" : undefined}>
      {renderMarkdown(revealed, onActivateRef)}
      {isRevealing ? (
        <span className="model-editor-chat-reveal-cursor" aria-hidden>
          ▍
        </span>
      ) : null}
    </span>
  )
}

